import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
  useTransition,
  Children,
  isValidElement,
} from 'react';
import { clickEndsPress, isPlainMousePress } from '../hooks/usePressAction'; // [FLUID-8 2026-10-12]
import ReactDOM from 'react-dom';
import styled, { css, keyframes } from 'styled-components';
import { duration, easing, transition, fluid } from '../styles/motion';
import { usePresence } from '../hooks/usePresence'; // [MOTION-A 2026-08-20]
import { fluidPop, fluidCascade } from './Fluid'; // [FLUID-1 2026-10-12]
import { pageRect, pageViewport } from '../utils/appZoom'; // [APP-FIT 2026-10-26] the popover is placed in page px
// [LIQUID-GLASS 2026-11-02, user] the opt-in liquid look (prop `liquid`)
import {
  lgPopover, lgControlBg, lgControlHover, lgCardSheen, lgRim, lgPress, lgHair, lgGreen, LG_POP,
} from '../styles/liquidGlass';

/**
 * Drop-in replacement for a themed `<select>`. Keeps the same JSX surface
 * (`<option>` children, `value`, `onChange={(e) => fn(e.target.value)}`) but
 * renders a custom liquid-glass button + popover listbox so the open dropdown
 * is also glassy — native `<option>` lists are rendered by the OS and can't
 * be blurred / rounded / shadowed.
 *
 * Behaviour preserved from a native select:
 *   • value / onChange semantics (synthetic event { target: { value } })
 *   • keyboard nav (ArrowUp / ArrowDown / Home / End / Enter / Esc)
 *   • type-ahead (typing letters jumps to the matching option)
 *   • click-outside / Escape closes the popover
 *   • accessible: combobox + listbox + option ARIA roles
 *
 * [SINGLE-SELECT] 2026-06-23 — single-select only. The opt-in multi-select mode
 * (`multiple` / `values` / `onChangeValues`) was removed so every dropdown in the
 * app behaves as single-value select; use a sentinel `<option value="">All …`
 * for the "no filter" choice instead of a multi clear-all row.
 *
 * [MULTI-MONTH 2026-07-15] Multi-select is BACK, but strictly OPT-IN via `multiple`.
 * Only the month filters on the list/filter tabs pass it; every other dropdown in
 * the app omits the prop and runs the untouched single-select path above, so the
 * 2026-06-23 rule still holds everywhere it was meant to.
 *
 * Multi mode contract:
 *   • `multiple` + `values: string[]` + `onChangeValues(next: string[])`
 *   • GESTURE [MULTI-TOGGLE 2026-08-06]: ONE click (or Enter/Space) adds or removes the
 *     option and the popover stays open — plain checkbox behaviour, mouse and keyboard
 *     alike. The old click-vs-double-click gesture (single = collapse to one and close,
 *     double = additive toggle) is GONE: it existed only for the multi-month filters,
 *     which reverted to single-select in 2026-07-28's `324ffed`, leaving it unreachable.
 *     Every month dropdown in the app is a plain SINGLE select — one click, one month.
 *   • popover STAYS OPEN while toggling (that's the point of multi)
 *   • rows look IDENTICAL to single-select rows — no checkbox, no left gutter;
 *     a ticked row just shows the same right-hand tick, rendered white.
 *     [AM-NAMES 2026-09-04, user] …UNLESS `multiCheckbox` is also passed, which swaps that
 *     for a real square checkbox in a left gutter. Opt-in precisely so this default — an
 *     explicit past request — stays the default everywhere that doesn't ask otherwise.
 *     Today only the Inventory "AM Names" cell editor asks.
 *   • the sentinel `<option value="">` acts as CLEAR-ALL → emits []
 *   • `values: []` means "no month filter" and the trigger shows the sentinel's label
 *   • OMIT the sentinel to make a selection REQUIRED: with no clear-all row there
 *     is no "none" state to fall back to, so the last ticked value can't be unticked
 *   • trigger footprint is identical to single-select: 1 selected → that label,
 *     N selected → "N months". It never grows with the selection, so toolbars
 *     that place this next to other controls don't reflow.
 */

interface OptionItem {
  value: string;
  label: React.ReactNode;
  text: string;          // plain string for type-ahead matching
}

interface Props {
  value?: string;
  onChange?: (e: { target: { value: string } }) => void;
  children?: React.ReactNode;
  disabled?: boolean;
  className?: string;
  title?: string;
  /** [SELECT-LOADING 2026-10-15, user] "when i click the function in the dropdown then it will show the loading icon on the same
   *  design": a turning ring in the arrow's place — the trigger keeps its text, size and colours (it is not disabled). */
  loading?: boolean;
  autoFocus?: boolean;
  /** Native-select compat: ignored — `required` validation isn't enforced here. */
  required?: boolean;
  /** Auto-open the popover on mount (inline cell-edit use case). */
  defaultOpen?: boolean;
  /** Fires whenever the popover closes (selection, outside click, Escape).
   *  [AM-NAMES 2026-09-04] `reason` is 'escape' when the user pressed Escape, and undefined
   *  for every other close. Additive — existing `() => …` handlers ignore it and are
   *  unaffected; only a handler that COMMITS on close needs to care. */
  onClose?: (reason?: 'escape') => void;

  /* ── [MULTI-MONTH 2026-07-15] opt-in multi-select ── */
  /** Turn on checkbox multi-select. Omit for the normal single-select behaviour. */
  multiple?: boolean;
  /** Selected values (multi mode only). `[]` = nothing selected → sentinel label shows. */
  values?: string[];
  /** Fires with the full next selection (multi mode only). */
  onChangeValues?: (next: string[]) => void;
  /** Noun for the "N <units>" trigger summary. Default 'months'. */
  multiUnitLabel?: string;
  /**
   * [AM-NAMES 2026-09-04, user] Draw a real square CHECKBOX in a left gutter instead of the
   * right-hand tick. OPT-IN, and deliberately so: the tick-only look was an explicit past
   * request for the month filters (see MULTI-MONTH above), so this must never become the
   * default — the AM Names cell passes it, every other multi dropdown keeps what it has.
   * Ignored unless `multiple` is set.
   */
  multiCheckbox?: boolean;

  /**
   * [FLUID-8 2026-10-12, user] "the fastness … the same to the complete application": OPT-IN for single-select PAGE
   * filters (a month, a person). A pick closes the list and shows the picked label AT ONCE, and the page's own update
   * (often a whole-page re-render: Process Analysis, EOM, PM Performance) follows as a low-priority transition — it used
   * to keep the list open and the old label up until that render was done. Never for form fields or cell editors: those
   * may be read straight after the pick (a Save right away), so they stay synchronous.
   */
  deferChange?: boolean;

  /**
   * [LIQUID-GLASS 2026-11-02, user] OPT-IN: the ColorOS 17 liquid glass look (styles/liquidGlass.ts), picked by the user
   * on the comparison page for the Payments filter buttons. The button is a glass capsule (light top edge, squish +
   * press light, a soft green glow while open); the list is real frosted glass, and a soft highlight slides to the row
   * under the mouse or the arrow keys. The blur sits on the list's outer box, which never scrolls (only the inner
   * row list scrolls): a blur on a box that scrolls its selected row into view while it grows is what flashed a white
   * box in Chrome, the reason the plain list below has no blur.
   */
  liquid?: boolean;

  /** [LIQUID-GLASS 2026-11-02] OPT-IN: only the LIST is liquid glass (frosted, sliding highlight); the button keeps the
   *  look its page gives it (the Add Payment window's Account field is drawn as one of its input boxes). */
  liquidList?: boolean;
}

/* ── Styled bits ── */

const Trigger = styled.button<{ $open: boolean; $disabled: boolean; $liquid?: boolean }>`
  appearance: none;
  -webkit-appearance: none;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  color: ${(p) => p.theme.text};
  cursor: ${(p) => (p.$disabled ? 'not-allowed' : 'pointer')};
  padding: 7px 34px 7px 12px;
  border-radius: 8px;
  min-width: 130px;
  text-align: left;
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: ${(p) => (p.$disabled ? 0.55 : 1)};

  background-color: ${(p) => (p.theme.colorScheme === 'dark'
    ? 'rgba(41, 41, 41, 0.55)'
    : 'rgba(255, 255, 255, 0.72)')};
  background-image: linear-gradient(180deg,
    rgba(255, 255, 255, ${(p) => (p.theme.colorScheme === 'dark' ? 0.07 : 0.55)}) 0%,
    rgba(255, 255, 255, 0) 60%);
  backdrop-filter: blur(14px) saturate(170%);
  -webkit-backdrop-filter: blur(14px) saturate(170%);

  border: 1px solid ${(p) => p.$open
    ? p.theme.green600
    : (p.theme.colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.10)' : 'rgba(22, 25, 23, 0.10)')};
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, ${(p) => (p.theme.colorScheme === 'dark' ? 0.10 : 0.65)}),
    0 1px 2px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(24,24,24,0.06)' : 'rgba(22, 25, 23, 0.06)')},
    ${(p) => (p.$open ? `0 0 0 3px ${p.theme.green600}33` : 'none')};

  transition: ${transition.hover};

  &:hover:not(:disabled) {
    border-color: ${(p) => p.$open
      ? p.theme.green600
      : (p.theme.colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.20)' : 'rgba(22, 25, 23, 0.18)')};
    background-color: ${(p) => (p.theme.colorScheme === 'dark'
      ? 'rgba(41, 41, 41, 0.68)'
      : 'rgba(255, 255, 255, 0.85)')};
  }

  /* [PHONE-LAYOUT 2026-10-22] Phones: a trimmer, taller trigger. 10 px left / 28 px right (the arrow keeps its place)
     leaves 8 px more room for the label, so "October 2026" or "All Accounts" fits a half-width filter cell at 360 px,
     and 9 px top / bottom makes the tap target 36 px tall. A page that sizes its own select with the doubled ampersand
     (pills, modal fields, cell editors) keeps its own padding. Desktop unchanged. */
  @media (max-width: 768px) {
    padding: 9px 28px 9px 10px;
  }

  /* [LIQUID-GLASS 2026-11-02, user] The glass capsule (see the liquid prop). Phones: 36 px tall, like the plain one. */
  ${(p) => p.$liquid && css`
    height: 34px;
    padding: 0 36px 0 15px;
    border: none;
    border-radius: 999px;
    min-width: 136px;
    background-color: ${lgControlBg(p)};
    background-image: ${lgCardSheen(p)};
    /* [STALE-PRESS 2026-11-02, user] No live blur of its own (it sits on a solid card, where a blur shows nothing): Chrome
       keeps a button with its own blur as a separate picture, and an old copy of it (with its press light) came back next
       to an open list as a piece cut by a straight line. */
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    ${lgRim}
    ${lgPress}
    box-shadow: ${p.$open
      ? `0 0 0 1px color-mix(in srgb, ${lgGreen(p)} 70%, transparent), 0 0 0 4px color-mix(in srgb, ${lgGreen(p)} 16%, transparent), 0 0 18px color-mix(in srgb, ${lgGreen(p)} 24%, transparent)`
      : (p.theme.colorScheme === 'dark' ? '0 1px 1px rgba(0,0,0,0.25), 0 10px 24px -12px rgba(0,0,0,0.6)' : '0 1px 2px rgba(22,25,23,0.06), 0 8px 20px -10px rgba(22,25,23,0.24)')};
    &:hover:not(:disabled) {
      background-color: ${lgControlHover(p)};
    }
    @media (max-width: 768px) {
      height: 36px;
      padding: 0 30px 0 12px;
    }
  `}
`;

const TriggerText = styled.span<{ $muted: boolean }>`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  opacity: ${(p) => (p.$muted ? 0.7 : 1)};
`;

const Chevron = styled.span<{ $open: boolean; $liquid?: boolean }>`
  position: absolute;
  right: ${(p) => (p.$liquid ? '13px' : '11px')};
  z-index: 1;
  top: 50%;
  width: 12px;
  height: 12px;
  margin-top: -6px;
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#d4d4d4' : '#505553')};
  transition: transform ${duration.fast} ${easing.emphasized};
  transform: rotate(${(p) => (p.$open ? '180deg' : '0deg')});
  display: inline-flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

/* [SELECT-LOADING 2026-10-15] the ring that stands in for the arrow while the trigger's work runs (same 12 px box) */
const spinRing = keyframes`
  to { transform: rotate(360deg); }
`;
const LoadingRing = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 1.6px solid currentColor;
  border-top-color: transparent;
  animation: ${spinRing} 0.8s linear infinite;
`;

const Popover = styled.div<{ $top: number; $left: number; $width: number; $open: boolean; $up: boolean; $align: 'left' | 'right'; $liquid?: boolean }>`
  position: fixed;
  top: ${(p) => p.$top}px;
  left: ${(p) => p.$left}px;
  min-width: ${(p) => p.$width}px;
  max-width: 360px;
  max-height: 320px;
  overflow-y: auto;
  z-index: 1200;
  border-radius: 12px;
  padding: 6px;

  /* Liquid-glass popover: top sheen + tint + bevel.
     NOTE: no backdrop-filter. This element both runs a transform open-animation
     (scaleY + translateY) AND scrolls the selected row into view on open — a
     backdrop-filter under that transform/scroll flashed a white box near the
     selected option (a Chromium backdrop-filter repaint glitch), on every page
     that uses this dropdown. The tint is near-opaque so it still reads as a
     solid glassy panel; the blur was barely visible behind it anyway. */
  background:
    linear-gradient(180deg,
      rgba(255, 255, 255, ${(p) => (p.theme.colorScheme === 'dark' ? 0.08 : 0.40)}) 0%,
      rgba(255, 255, 255, 0) 40%
    ),
    ${(p) => (p.theme.colorScheme === 'dark'
      ? 'rgba(38, 38, 38, 0.985)'
      : 'rgba(250, 251, 253, 0.985)')};

  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark'
    ? 'rgba(255, 255, 255, 0.12)'
    : 'rgba(22, 25, 23, 0.10)')};

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, ${(p) => (p.theme.colorScheme === 'dark' ? 0.14 : 0.6)}),
    0 16px 40px rgba(15, 23, 42, ${(p) => (p.theme.colorScheme === 'dark' ? 0.55 : 0.18)}),
    0 2px 6px rgba(15, 23, 42, ${(p) => (p.theme.colorScheme === 'dark' ? 0.40 : 0.10)});

  /* [FLUID-1 2026-10-12, user] Grows out of the trigger's corner on the spring and its rows rise in
     one after another (components/Fluid.tsx); closing is quicker. Replaces the old scaleY(0.96)
     180 ms reveal. The corner follows the placement below: left or right edge, below or above. */
  ${(p) => fluidPop({
    open: p.$open,
    origin: `${p.$align === 'right' ? '100%' : '0'} ${p.$up ? '100%' : '0'}`,
    from: `translateY(${p.$up ? 8 : -8}px) scale(0.92)`,
  })}
  ${(p) => (p.$open ? fluidCascade('[role="option"]', 0, 12, 10, 'soft') /* [FLUID-7 2026-10-12] at once, 12 ms apart */ : '')}

  /* Custom scrollbar so it doesn't break the glass illusion */
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb {
    background: ${(p) => (p.theme.colorScheme === 'dark'
      ? 'rgba(255, 255, 255, 0.18)' : 'rgba(22, 25, 23, 0.18)')};
    border-radius: 3px;
  }

  /* [PHONE-LAYOUT 2026-10-22] Phones: the list is as wide as its longest option (never narrower than the trigger) but
     never wider than the screen, 8 px clear of each edge (was a fixed 360 px cap: 8 px past the edge of a 360 px phone).
     An option longer than that wraps (OptionRow below) instead of ending in an ellipsis. Desktop unchanged. */
  @media (max-width: 768px) {
    width: max-content;
    max-width: calc(100vw - 16px);
  }

  /* [LIQUID-GLASS 2026-11-02, user] The glass list, 18 px corners. This outer box does NOT scroll (LiquidList inside it
     does). [STALE-PRESS 2026-11-02, user] Without the live blur (lgPopover), for the reason in the NOTE above: the blur
     made Chrome redraw the buttons around the open list from old pictures (a cut line on the other filter buttons). */
  ${(p) => p.$liquid && css`
    max-height: none;
    overflow: hidden;
    padding: 0;
    border: none;
    border-radius: 18px;
    ${lgPopover}
  `}
`;

/* [LIQUID-GLASS 2026-11-02] The scrolling row list inside a liquid popover, and the soft highlight that slides (on a
   small spring) to the active row: the row under the mouse, or the one the arrow keys reached. */
const LiquidList = styled.div`
  position: relative;
  max-height: 320px;
  overflow-y: auto;
  padding: 6px;
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb {
    background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.18)' : 'rgba(22, 25, 23, 0.18)')};
    border-radius: 3px;
  }
`;

const LiquidHighlight = styled.div<{ $on: boolean }>`
  position: absolute;
  left: 6px;
  right: 6px;
  top: 0;
  height: 34px;
  border-radius: 12px;
  background: ${lgControlHover};
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.22), 0 0 0 1px ${lgHair};
  opacity: ${(p) => (p.$on ? 1 : 0)};
  pointer-events: none;
  z-index: 0;
  transition: transform 340ms ${LG_POP}, height 200ms ease, opacity 150ms ease;
  @media (prefers-reduced-motion: reduce) {
    transition: opacity 150ms ease;
  }
`;

const OptionRow = styled.button<{ $selected: boolean; $active: boolean; $multi?: boolean; $box?: boolean; $liquid?: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  background: ${(p) => p.$active
    ? (p.theme.colorScheme === 'dark' ? 'rgba(14, 126, 78, 0.30)' : 'rgba(26, 127, 100, 0.14)')
    : p.$selected
      ? (p.theme.colorScheme === 'dark' ? 'rgba(14, 126, 78, 0.18)' : 'rgba(26, 127, 100, 0.08)')
      : 'transparent'};
  color: ${(p) => p.theme.text};
  font-size: 13px;
  font-weight: ${(p) => (p.$selected ? 600 : 500)};
  padding: 8px 12px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  position: relative;
  transition: background-color ${duration.micro} ${easing.standard};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:focus { outline: none; }

  /* Subtle "selected" tick on the right — shown for the chosen row.
     [MULTI-MONTH 2026-07-15] Multi mode uses this SAME right-hand tick (no
     checkbox, no left gutter) so a multi row is visually identical to a
     single-select row — just rendered WHITE, per request. Single-select keeps
     its original green tick, untouched. */
  &::after {
    content: ${(p) => (p.$selected ? `'\\f00c'` : `''`)};
    font-family: 'Font Awesome 6 Free', 'Font Awesome 5 Free', FontAwesome;
    font-weight: 900;
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 10px;
    /* [MULTI-TICK 2026-08-06] White reads well on the dark popover, but on the LIGHT
       theme it was white-on-near-white — the ticks were invisible, so a multi-select
       gave no feedback about what was chosen. Keep white in dark, fall back to the
       single-select green in light. */
    color: ${(p) => (p.$multi && p.theme.colorScheme === 'dark' ? '#ffffff' : p.theme.green600)};
    opacity: ${(p) => (p.$selected ? 1 : 0)};
  }

  /* [PHONE-LAYOUT 2026-10-22] Phones: 36 px tall rows (easier to tap), 28 px clear on the right for the tick, and a long
     option wraps onto a second line instead of being cut with an ellipsis. Longhand paddings on purpose: the checkbox
     rows below keep their 34 px left gutter. Desktop unchanged. */
  @media (max-width: 768px) {
    padding-top: 10px;
    padding-bottom: 10px;
    padding-right: 28px;
    white-space: normal;
    overflow-wrap: anywhere;
    text-overflow: clip;
  }

  /* [AM-NAMES 2026-09-04, user] Opt-in checkbox mode (multiCheckbox). A real square in a
     left gutter, with the tick moved inside it — asked for by name, and scoped to the
     dropdowns that request it so the month filters keep their tick-only rows. An unticked
     row still draws the empty box, which is the whole point: you can see at a glance that
     the list is multi-select before touching anything. */
  ${(p) => p.$box && css`
    padding-left: 34px;

    &::before {
      content: '';
      position: absolute;
      left: 11px;
      top: 50%;
      transform: translateY(-50%);
      width: 15px;
      height: 15px;
      border-radius: 4px;
      box-sizing: border-box;
      border: 1.5px solid ${p.$selected
        ? p.theme.green600
        : (p.theme.colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.30)' : 'rgba(22, 25, 23, 0.28)')};
      background: ${p.$selected ? p.theme.green600 : 'transparent'};
      transition: background-color ${duration.micro} ${easing.standard},
                  border-color ${duration.micro} ${easing.standard};
    }

    /* Same tick glyph, relocated into the box and always white — it now sits on the
       filled green square in BOTH themes, so the light-theme contrast note above
       (which is about a tick on the popover background) does not apply here. */
    &::after {
      left: 15px;
      right: auto;
      color: #ffffff;
      font-size: 9px;
    }
  `}

  /* [LIQUID-GLASS 2026-11-02] In a liquid list the sliding highlight (LiquidHighlight) marks the active row, so the row
     itself stays clear; it sits above the highlight. */
  ${(p) => p.$liquid && css`
    background: transparent;
    z-index: 1;
    border-radius: 12px;
    padding-top: 9px;
    padding-bottom: 9px;
  `}
`;

// Pull the plain-text label out of an <option>'s children for type-ahead matching —
// recurses through arrays and element wrappers (e.g. a coloured <span>{label}</span>),
// so a styled option label still matches what the user types.
function extractOptionText(node: any): string {
  if (node == null || node === false || node === true) return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractOptionText).join('');
  if (isValidElement(node)) return extractOptionText((node.props as any)?.children);
  return '';
}

/* ── Component ── */

const GlassSelect: React.FC<Props> = ({
  value = '', onChange, children, disabled, className, title, loading = false, autoFocus, defaultOpen, onClose,
  multiple = false, values, onChangeValues, multiUnitLabel = 'months', multiCheckbox = false, deferChange = false,
  liquid = false, liquidList = false,
}) => {
  const glassList = liquid || liquidList; // [LIQUID-GLASS 2026-11-02] the frosted list with the sliding highlight
  const [open, setOpenRaw] = useState(!!defaultOpen);
  // [MOTION-A 2026-08-20] The popover used to mount straight into its open state and unmount
  // the instant it closed, so its `transition.popover` never played in EITHER direction.
  // Presence keeps it mounted through the exit and flips $open one frame after mount, so the
  // existing transition now runs both ways. `open` stays the source of truth for behaviour.
  const popPres = usePresence(open, fluid.outMs); // [FLUID-1 2026-10-12] held for the 200 ms close
  // [FLUID-7 2026-10-12] the mouse press already toggled the list (the click that follows must not).
  // [CELL-PRESS 2026-10-13] A list that MOUNTS open (a table cell's editor, opened on that cell's mouse press) may get the
  // release of that very press as a click on its own button (the button is now under the pointer) — it must not close it,
  // so it starts as if pressed. Any later real click comes with its own press (which resets this); a keyboard click
  // (detail 0) always acts.
  const pressedRef = useRef(!!defaultOpen);
  // [FLUID-8 2026-10-12] deferChange: the value just picked, shown until the page's update has landed (or was refused).
  const [localPick, setLocalPick] = useState<string | null>(null);
  const [pickPending, startPick] = useTransition();
  useEffect(() => { if (!pickPending) setLocalPick(null); }, [pickPending, value]);
  const [activeIdx, setActiveIdx] = useState(-1);
  // [FLUID-1 2026-10-12] up / align = which corner of the trigger the popover grows from.
  const [pos, setPos] = useState<{ top: number; left: number; width: number; up: boolean; align: 'left' | 'right' }>(
    { top: 0, left: 0, width: 0, up: false, align: 'left' },
  );
  const typeBufRef = useRef<string>('');
  const typeTimerRef = useRef<number | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  // [AM-NAMES 2026-09-04] Why the popover is closing, read once by the notifier below.
  // A multi-select cell editor COMMITS what you ticked when the popover closes, so it has
  // to be able to tell "clicked away" (commit) from "pressed Escape" (abandon) — without
  // it, Escape would save the very edit the user just backed out of.
  const closeReasonRef = useRef<'escape' | undefined>(undefined);

  // Wrap setOpen so consumers get notified whenever the popover closes — the
  // inline cell-edit flow needs this to cancel the edit on outside click.
  const setOpen = useCallback((next: boolean | ((p: boolean) => boolean)) => {
    setOpenRaw((prev) => {
      const v = typeof next === 'function' ? (next as (p: boolean) => boolean)(prev) : next;
      if (prev && !v) {
        const reason = closeReasonRef.current;
        closeReasonRef.current = undefined; // consume it — the next close is its own event
        onClose?.(reason);
      }
      return v;
    });
  }, [onClose]);

  // Walk <option> children → flat list
  const options = useMemo<OptionItem[]>(() => {
    const out: OptionItem[] = [];
    Children.forEach(children, (child) => {
      if (!isValidElement(child)) return;
      // ts-ignore: native option child
      const ct: any = child.type;
      if (ct !== 'option') return;
      const p: any = child.props;
      const raw = p.children;
      const text = extractOptionText(raw);
      out.push({ value: String(p.value ?? ''), label: raw, text });
    });
    return out;
  }, [children]);

  const shownValue = localPick ?? value; // [FLUID-8 2026-10-12] deferChange: the pick shows at once
  const selectedIdx = useMemo(() => options.findIndex((o) => o.value === String(shownValue)), [options, shownValue]);
  const selected = selectedIdx >= 0 ? options[selectedIdx] : undefined;

  // [MULTI-MONTH 2026-07-15] Multi-mode selection set. Empty in single mode, so
  // every helper below collapses to the original behaviour when `multiple` is off.
  const effectiveValues = useMemo(
    () => (multiple ? (values ?? []) : []),
    [multiple, values],
  );
  const valueSet = useMemo(() => new Set(effectiveValues), [effectiveValues]);
  /** Is this row ticked? Single mode defers to the original selectedIdx match. */
  const isRowSelected = useCallback(
    (idx: number) => (multiple ? valueSet.has(options[idx]?.value ?? ' ') : selectedIdx === idx),
    [multiple, valueSet, options, selectedIdx],
  );

  /* Position the popover under the trigger, clamping inside the viewport so
     it never gets clipped on the right or left edge. */
  const recompute = useCallback(() => {
    const tr = triggerRef.current;
    if (!tr) return;
    // [APP-FIT 2026-10-26] In page px (utils/appZoom): the popover's top / left / width are page px under the app zoom.
    const r = pageRect(tr);
    const margin = 8;
    const { width: vw, height: vh } = pageViewport();

    // Use the live popover width if it's already rendered, otherwise estimate
    // from the trigger width clamped to Popover's max-width.
    const pop = popoverRef.current;
    const popW = pop?.offsetWidth ?? Math.min(360, Math.max(r.width, 200));
    const popH = pop?.offsetHeight ?? 0;

    let left = r.left;
    let align: 'left' | 'right' = 'left';
    if (left + popW + margin > vw) {
      // Right-align to trigger when the popover would otherwise overflow right
      left = Math.max(margin, r.right - popW);
      align = 'right';
    }
    if (left < margin) left = margin;

    // Flip above the trigger if there isn't enough room below
    let top = r.bottom + 6;
    let up = false;
    if (popH && top + popH + margin > vh && r.top - popH - 6 > margin) {
      top = r.top - popH - 6;
      up = true;
    }

    setPos({ top, left, width: r.width, up, align });
  }, []);

  // Synchronous position pass — runs after DOM mutations but before browser
  // paint, so the popover never flashes at the wrong coordinates when it
  // first opens near a screen edge.
  useLayoutEffect(() => {
    if (!open) return;
    recompute();
  }, [open, recompute]);

  // [FLUID-1 2026-10-12] Measure again once the list is really in the DOM. The pass above runs one render BEFORE
  // presence mounts the list, so it could only estimate the size and never flipped a list ABOVE a button near the
  // bottom of the screen — since 2026-08-20 such a list ran off the bottom edge (measured: 850–1184 px on an 860 px
  // screen). Still before paint, so the list's first frame is already in the right place, growing from the right corner.
  useLayoutEffect(() => {
    if (open && popPres.mounted) recompute();
  }, [open, popPres.mounted, recompute]);

  useEffect(() => {
    if (!open) return;
    setActiveIdx(selectedIdx >= 0 ? selectedIdx : 0);
    // Focus the popover so arrow/enter/escape land here, not on the page.
    requestAnimationFrame(() => popoverRef.current?.focus());

    // Only close on scrolls that AREN'T inside the popover itself —
    // otherwise mouse-wheel on the list immediately closes it.
    const onScroll = (e: Event) => {
      if (popoverRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onResize = () => recompute();
    const onDocClick = (e: MouseEvent) => {
      if (
        !triggerRef.current?.contains(e.target as Node) &&
        !popoverRef.current?.contains(e.target as Node)
      ) setOpen(false);
    };

    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    document.addEventListener('mousedown', onDocClick);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('mousedown', onDocClick);
    };
  }, [open, recompute, selectedIdx]);

  /* Scroll the active row into view */
  useEffect(() => {
    if (!open || activeIdx < 0) return;
    const el = popoverRef.current?.querySelector<HTMLElement>(`[data-idx="${activeIdx}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIdx]);

  // [LIQUID-GLASS 2026-11-02] Where the sliding highlight sits: over the active row (its top and height inside the list).
  const listRef = useRef<HTMLDivElement>(null);
  const [hl, setHl] = useState<{ y: number; h: number } | null>(null);
  useLayoutEffect(() => {
    if (!glassList || !open || !popPres.mounted || activeIdx < 0) { setHl(null); return; }
    const el = listRef.current?.querySelector<HTMLElement>(`[data-idx="${activeIdx}"]`);
    if (!el) { setHl(null); return; }
    const next = { y: el.offsetTop, h: el.offsetHeight };
    setHl((prev) => (prev && prev.y === next.y && prev.h === next.h ? prev : next));
  }, [glassList, open, popPres.mounted, activeIdx, options]);

  const fire = (v: string) => {
    onChange?.({ target: { value: v } });
  };

  /** Emit in the options' own order so consumers get a stable, sorted list rather
   *  than click order — keeps API params / cache keys deterministic. */
  const orderedFrom = useCallback(
    (set: Set<string>) => options.filter((o) => o.value !== '' && set.has(o.value)).map((o) => o.value),
    [options],
  );

  /** Additive toggle against `base`, honouring the require-one rule. */
  const toggledFrom = useCallback((base: string[], val: string): string[] => {
    const set = new Set(base);
    if (set.has(val)) {
      // No sentinel in the list => "no selection" isn't a reachable state, so refuse
      // to untick the last one; otherwise the trigger would fall back to
      // "All <units>", a state the consumer explicitly removed.
      const hasSentinel = options.some((o) => o.value === '');
      if (!hasSentinel && set.size === 1) return base;
      set.delete(val);
    } else set.add(val);
    return orderedFrom(set);
  }, [options, orderedFrom]);

  /** [MULTI-TOGGLE 2026-08-06] The ONE multi-select gesture, for both mouse and
   *  keyboard: toggle the option additively, immediately, and leave the popover open.
   *  The sentinel row ("All …") stays a clear-all. */
  const toggleMulti = (idx: number) => {
    const opt = options[idx];
    if (!opt) return;
    if (opt.value === '') { onChangeValues?.([]); return; }
    onChangeValues?.(toggledFrom(values ?? [], opt.value));
  };

  const pick = (idx: number) => {
    const opt = options[idx];
    if (!opt) return;
    // [MULTI-TOGGLE 2026-08-06] Multi mode toggles and stays open; single mode picks
    // one value and closes. Nothing waits on a double-click window any more.
    if (multiple) { toggleMulti(idx); return; }
    if (deferChange) {
      // [FLUID-8 2026-10-12] Close and show the pick now; the page's update follows as a transition (see deferChange).
      setLocalPick(opt.value);
      setOpen(false);
      triggerRef.current?.focus();
      startPick(() => fire(opt.value));
      return;
    }
    fire(opt.value);
    setOpen(false);
    triggerRef.current?.focus();
  };

  const onTriggerKey = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen(true);
      return;
    }
  };

  const onPopoverKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); closeReasonRef.current = 'escape'; setOpen(false); triggerRef.current?.focus(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(options.length - 1, i + 1)); return; }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIdx((i) => Math.max(0, i - 1)); return; }
    if (e.key === 'Home')      { e.preventDefault(); setActiveIdx(0); return; }
    if (e.key === 'End')       { e.preventDefault(); setActiveIdx(options.length - 1); return; }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      // [MULTI-TOGGLE 2026-08-06] Keyboard and mouse now share one path — pick()
      // routes multi mode to toggleMulti itself, so Enter/Space needs no special case.
      pick(activeIdx);
      return;
    }

    // Type-ahead — a typed run of characters jumps to the matching option
    if (e.key.length === 1) {
      typeBufRef.current += e.key.toLowerCase();
      if (typeTimerRef.current) window.clearTimeout(typeTimerRef.current);
      typeTimerRef.current = window.setTimeout(() => { typeBufRef.current = ''; }, 600);
      const hit = options.findIndex((o) => o.text.toLowerCase().startsWith(typeBufRef.current));
      if (hit >= 0) setActiveIdx(hit);
    }
  };

  // Trigger text shows the chosen option (falling back to the first option, which
  // is typically the "All …" entry, when nothing matches the current value).
  // [MULTI-MONTH 2026-07-15] Multi mode collapses the selection to a FIXED-WIDTH
  // summary — 0 → sentinel label ("All Months"), 1 → that month, N → "N months".
  // Deliberately never lists the months: the trigger must not grow with the
  // selection or the surrounding toolbar reflows (and wraps on mobile).
  let triggerLabel: React.ReactNode;
  let triggerMuted: boolean;
  if (multiple) {
    const n = valueSet.size;
    if (n === 0) {
      triggerLabel = options.find((o) => o.value === '')?.label ?? `All ${multiUnitLabel}`;
      triggerMuted = true;
    } else if (n === 1) {
      triggerLabel = options.find((o) => o.value === [...valueSet][0])?.label ?? `1 ${multiUnitLabel}`;
      triggerMuted = false;
    } else {
      triggerLabel = `${n} ${multiUnitLabel}`;
      triggerMuted = false;
    }
  } else {
    triggerLabel = selected ? selected.label : (options[0]?.label ?? '');
    triggerMuted = !selected;
  }

  return (
    <>
      <Trigger
        ref={triggerRef}
        type="button"
        $open={open}
        $disabled={!!disabled}
        disabled={disabled}
        autoFocus={autoFocus}
        // [FLUID-7 2026-10-12, user] "when I click on dropdowns it shows a little delay and then it works": the list opened
        // on the click, which only fires when the mouse button comes back UP (~0.1 s into a normal click). A mouse now
        // opens it on the PRESS, as the browser's own dropdowns do; touch and pen keep the click (a press there may be the
        // start of a scroll), and the keyboard keeps its keys (onTriggerKey).
        onPointerDown={(e) => {
          pressedRef.current = isPlainMousePress(e) && !disabled; // [FLUID-8 2026-10-12] the shared press rule
          if (pressedRef.current) setOpen((o) => !o);
        }}
        onClick={(e) => {
          if (clickEndsPress(pressedRef, e)) return; // the press already opened / closed it
          if (!disabled) setOpen((o) => !o);
        }}
        onKeyDown={onTriggerKey}
        className={className}
        title={title}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-busy={loading || undefined}
        $liquid={liquid}
        data-lg-press={liquid ? '' : undefined}
      >
        <TriggerText $muted={triggerMuted}>{triggerLabel}</TriggerText>
        <Chevron $open={open && !loading} $liquid={liquid} aria-hidden="true" data-select-loading={loading ? '' : undefined}>
          {loading ? <LoadingRing /> : (
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </Chevron>
      </Trigger>

      {popPres.mounted && ReactDOM.createPortal(
        <Popover
          ref={popoverRef}
          $top={pos.top}
          $left={pos.left}
          $width={pos.width}
          $up={pos.up}
          $align={pos.align}
          $open={open && popPres.entered}
          $liquid={glassList}
          onKeyDown={onPopoverKey}
          tabIndex={-1}
          role="listbox"
          aria-multiselectable={multiple || undefined}
        >
          {(() => {
            const rows = options.map((o, idx) => (
              <OptionRow
                key={`${o.value}-${idx}`}
                type="button"
                data-idx={idx}
                role="option"
                aria-selected={isRowSelected(idx)}
                $selected={isRowSelected(idx)}
                $active={activeIdx === idx}
                $multi={multiple}
                /* [AM-NAMES 2026-09-04] Checkbox rows are multi-only — a single-select list
                   with tickboxes would promise a selection it cannot hold. */
                $box={multiple && multiCheckbox}
                $liquid={glassList}
                onMouseEnter={() => setActiveIdx(idx)}
                onMouseDown={(e) => e.preventDefault()} // keep focus on popover for keyboard
                onClick={() => pick(idx)}
              >
                {o.label}
              </OptionRow>
            ));
            // [LIQUID-GLASS 2026-11-02] the rows scroll inside LiquidList, over the sliding highlight
            return glassList ? (
              <LiquidList ref={listRef}>
                <LiquidHighlight
                  aria-hidden="true"
                  $on={!!hl}
                  style={hl ? { transform: `translateY(${hl.y}px)`, height: hl.h } : undefined}
                />
                {rows}
              </LiquidList>
            ) : rows;
          })()}
        </Popover>,
        document.body
      )}
    </>
  );
};

export default GlassSelect;
